// geometriaDeMezcla.ts — la geometría de las líneas de una mezcla
// (ADR-2026-09-17-las-partes-se-atan-con-una-linea, ADR-2026-09-17-las-lineas-salen-en-curva):
// una curva Bézier cúbica que SALE EN HORIZONTAL del costado de la prenda que mira a la
// parte y ENTRA EN HORIZONTAL por el costado de la parte que mira a la prenda, como los
// edges de React Flow entre puertos izquierdo y derecho. PURO, en coordenadas del lienzo
// (las de los nodos, sin zoom), para probarlo.

export interface Rectangulo {
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

export interface Curva {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** El `d` del `<path>`: `M x1 y1 C c1x y1, c2x y2, x2 y2` (tangentes horizontales). */
  d: string;
}

/** Cuánto se abre la curva a los costados antes de doblar (misma fórmula que React Flow). */
const CURVATURA = 0.25;
/** El mínimo, para que la salida se vea horizontal aunque las celdas estén pegadas. */
const CONTROL_MINIMO = 24;

function controlDeCurva(distancia: number): number {
  const c = distancia >= 0 ? 0.5 * distancia : CURVATURA * 25 * Math.sqrt(-distancia);
  return Math.max(CONTROL_MINIMO, c);
}

const redondear = (n: number): number => Math.round(n * 100) / 100;

/** La curva del costado de `desde` al costado de `hasta`. */
export function curvaEntreRectangulos(desde: Rectangulo, hasta: Rectangulo): Curva {
  const haciaLaDerecha = hasta.x + hasta.ancho / 2 >= desde.x + desde.ancho / 2;
  const dir = haciaLaDerecha ? 1 : -1;
  const x1 = haciaLaDerecha ? desde.x + desde.ancho : desde.x;
  const y1 = desde.y + desde.alto / 2;
  const x2 = haciaLaDerecha ? hasta.x : hasta.x + hasta.ancho;
  const y2 = hasta.y + hasta.alto / 2;
  const control = controlDeCurva((x2 - x1) * dir);
  const c1x = x1 + dir * control;
  const c2x = x2 - dir * control;
  const d = `M ${redondear(x1)} ${redondear(y1)} C ${redondear(c1x)} ${redondear(y1)}, ${redondear(c2x)} ${redondear(y2)}, ${redondear(x2)} ${redondear(y2)}`;
  return { x1, y1, x2, y2, d };
}

export interface NodoMedido {
  id: string;
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

/** Una curva por parte, en el orden de las partes; las que no están en el lienzo se saltean. */
export function lineasDeMezcla(nodos: NodoMedido[], baseId: string, parteIds: readonly string[]): (Curva & { id: string })[] {
  const porId = new Map(nodos.map((n) => [n.id, n]));
  const base = porId.get(baseId);
  if (!base) return [];
  const out: (Curva & { id: string })[] = [];
  for (const id of parteIds) {
    const n = porId.get(id);
    if (!n || n === base) continue;
    out.push({ id, ...curvaEntreRectangulos(base, n) });
  }
  return out;
}
